import styled from 'styled-components'
import useTranslation from '../../hooks/useTranslation'
import { formatValue, mergeFields } from './common'

const MasterCard = styled.div`
    background: ${({ theme }) => theme?.card?.background || '#ffffff'};
    border-radius: 12px;
    box-shadow: ${({ theme }) => theme?.card?.boxShadow || '0 2px 8px rgba(0, 0, 0, 0.1)'};
    padding: 1.5rem;
    width: 100%;
`

const MasterSection = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;
    width: 100%;
    
    ${({ $separator, theme }) => $separator !== false && `
        &:not(:last-child) {
            margin-bottom: 1.5rem;
            padding-bottom: 1.5rem;
            border-bottom: 1px solid ${theme?.card?.borderColor || 'rgba(0, 0, 0, 0.1)'};
        }
    `}
`

const MasterColumns = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 2rem;
    width: 100%;
`

const MasterColumn = styled.div`
    flex: 1 1 0;
    min-width: ${({ $length }) => $length || '100px'};
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`

const MasterRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`

const RowLabel = styled.span`
    font-size: var(--font-XXS);
    color: ${({ theme }) => theme?.font?.label || '#515559'};
    text-transform: uppercase;
    letter-spacing: 0.5px;
`

const RowValue = styled.span`
    font-size: ${({ $size }) => $size === 'title' ? 'var(--font-L)' : 'var(--font-XS)'};
    font-weight: ${({ $size }) => $size === 'title' ? '600' : '400'};
    color: ${({ theme }) => theme?.font?.color || '#333'};
`

/**
 * Render columns for a section
 */
const renderColumns = (columns, data, t) => {
    return columns.map((column, colIndex) => (
        <MasterColumn key={colIndex} $length={column.width}>
            {column.rows?.map((row, rowIndex) => {
                const displayValue = mergeFields(row.mergeFields, data, row.formats, t)
                return (
                    <MasterRow key={rowIndex}>
                        <RowLabel>{t(row.label)}</RowLabel>
                        <RowValue $size={row.scale}>{displayValue}</RowValue>
                    </MasterRow>
                )
            })}
        </MasterColumn>
    ))
}

/**
 * MasterView Component
 * 
 * Displays master entity data as an info card with sections, columns and rows.
 * Supports both legacy `columns` format and new `sections` format.
 */
const MasterView = ({ config, data }) => {
    const { t } = useTranslation()
    
    if (!config || !data) {
        return null
    }
    
    // Support new sections-based layout
    const sections = config.sections || []
    
    // Fallback to legacy columns format if no sections
    if (sections.length === 0 && config.columns) {
        return (
            <MasterCard>
                <MasterColumns>
                    {renderColumns(config.columns, data, t)}
                </MasterColumns>
            </MasterCard>
        )
    }
    
    return (
        <MasterCard>
            {sections.map((section, sectionIndex) => (
                <MasterSection key={sectionIndex} $separator={section.separator}>
                    <MasterColumns>
                        {renderColumns(section.columns || [], data, t)}
                    </MasterColumns>
                </MasterSection>
            ))}
        </MasterCard>
    )
}

export default MasterView
